import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUp,
  ArrowLeft,
  Lock,
  Shield,
  Eye,
  Users,
  Database,
  Clock,
  MapPin,
  Mail,
  Phone,
  FileText,
  Zap,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Cookie,
  Share2,
  Trash2,
  Key,
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/TermsAndServices.css';

gsap.registerPlugin(ScrollTrigger);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('introduction');
  const [expandedSections, setExpandedSections] = useState({});
  const [scrollProgress, setScrollProgress] = useState(0);
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const sectionsRef = useRef({});

  useEffect(() => {
    window.scrollTo(0, 0);

    gsap.fromTo(
      headerRef.current,
      { opacity: 0, y: -40 },
      { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }
    );

    Object.values(sectionsRef.current).forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 25 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            delay: 0.08 + index * 0.04,
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    });

    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        const element = sectionsRef.current[hash];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveSection(hash);
        }
      }, 100);
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);

      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (window.scrollY / docHeight) * 100;
      setScrollProgress(scrolled);

      Object.keys(sectionsRef.current).forEach((key) => {
        const element = sectionsRef.current[key];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top < window.innerHeight / 3) {
            setActiveSection(key);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mainSections = [
    {
      id: 'introduction',
      title: 'Your Privacy Matters to Us',
      icon: <Eye size={24} />,
      type: 'highlight',
      tldr: 'We respect your privacy and are committed to protecting your personal data.',
      ctaText: 'Learn More',
      content:
        'At Sync and Explore, we believe privacy is a fundamental right. This Privacy Policy explains what information we collect, how we use it, and the choices you have about your data. We are transparent about our data practices and committed to complying with all applicable privacy laws including GDPR, CCPA, and others.',
    },
    {
      id: 'collection',
      title: 'What Information Do We Collect?',
      icon: <Database size={24} />,
      type: 'primary',
      tldr: 'We collect information you provide and data generated from your use of our platform.',
      ctaText: 'Details',
      content:
        'We collect information in several ways to provide you with the best travel planning experience.',
      bullets: [
        'Account Information: Name, email, phone number, profile picture, date of birth, and travel preferences',
        'Calendar Data: Your calendar events and availability when you opt-in to calendar sync',
        'Payment Information: Processed securely through third-party payment providers (we do not store credit card details)',
        'Travel Details: Destinations, trip dates, budget, travel companions, accommodation preferences',
        'Communication Data: Messages, support tickets, and feedback you send us',
        'Device Information: Device type, browser, IP address, operating system, and mobile advertising identifiers',
        'Location Data: General location information (with your permission) for personalized recommendations',
        'Cookies & Tracking: Usage patterns, session data, and preferences via cookies and similar technologies',
        'Social Media Data: If you connect social accounts, we collect public profile information',
      ],
    },
    {
      id: 'usage',
      title: 'How We Use Your Information',
      icon: <Zap size={24} />,
      type: 'primary',
      tldr: 'We use your data to provide services, improve our platform, and personalize your experience.',
      ctaText: 'Expand',
      content:
        'Your personal information helps us deliver a personalized and seamless travel planning experience.',
      bullets: [
        'Service Delivery: Process bookings, send confirmations, and provide customer support',
        'AI Recommendations: Personalize destination and travel buddy recommendations based on your preferences',
        'Calendar Synchronization: Match your available travel dates with optimal destinations',
        'User Experience: Enhance interface design, fix bugs, and optimize platform performance',
        'Communication: Send important updates, newsletters (if opted-in), and travel tips',
        'Safety & Security: Detect fraudulent activities, prevent misuse, and ensure account security',
        'Legal Compliance: Meet regulatory requirements and resolve disputes',
        'Marketing & Analytics: Understand user behavior and improve our products (with consent)',
        'Community Features: Enable buddy matching and group trip organization',
      ],
    },
    {
      id: 'sharing',
      title: 'Who Do We Share Your Data With?',
      icon: <Share2 size={24} />,
      type: 'primary',
      tldr: 'We only share data with trusted partners necessary for service delivery.',
      ctaText: 'Details',
      content:
        'We do not sell your personal data. However, we may share information with trusted partners for specific purposes.',
      bullets: [
        'Travel Partners: Hotels, airlines, and booking platforms to complete your reservations',
        'Payment Processors: Stripe, PayPal, and other payment gateways for secure transactions',
        'Calendar Services: Google Calendar, Microsoft Outlook for synchronization (only with your consent)',
        'Analytics Providers: Google Analytics, Mixpanel to understand user behavior',
        'Cloud Providers: AWS, Firebase for secure data storage and backup',
        'Customer Support Tools: Zendesk, Intercom for managing support tickets',
        'Legal Authorities: Only when required by law or to protect user safety',
        'Service Providers: Contractors and vendors who assist with our operations (under strict NDAs)',
        'Business Partners: In case of merger or acquisition (with prior notification)',
      ],
    },
    {
      id: 'google-data-access',
      title: 'Google User Data We Access',
      icon: <Database size={24} />,
      type: 'primary',
      tldr: 'We access specific Google data only with your permission and for calendar synchronization.',
      ctaText: 'Details',
      content:
        'When you choose to connect your Google account with Sync and Explore, we may access certain Google user data strictly with your permission and in accordance with the Google API Services User Data Policy.',
      bullets: [
        'Google Calendar data, including: Calendar event titles, Event dates and times, Availability status, Event descriptions (if provided by you), Calendar IDs associated with your account',
        'Access is limited to calendar data only - we do not access Gmail, Drive, Contacts, or other Google services',
        'All access requires your explicit consent through Google OAuth',
        'You can revoke access at any time through your Google account settings',
      ],
    },
    {
      id: 'google-data-usage',
      title: 'How We Use Google User Data',
      icon: <Zap size={24} />,
      type: 'primary',
      tldr: 'Google data is used solely for calendar synchronization and travel planning.',
      ctaText: 'Learn More',
      content:
        'We use Google Calendar data solely for the following purposes to enhance your travel planning experience.',
      bullets: [
        'To synchronize your calendar with Sync and Explore for accurate availability tracking',
        'To understand your availability for travel planning and trip recommendations',
        'To suggest suitable travel dates based on your existing schedule',
        'To avoid scheduling conflicts when recommending trips and activities',
        'To enhance your personalized travel experience through better planning',
        'We do not use Google user data for advertising, selling, or training AI models',
        'All processing is done locally on your device when possible, with minimal server-side storage',
      ],
    },
    {
      id: 'google-data-sharing',
      title: 'Sharing of Google User Data',
      icon: <Share2 size={24} />,
      type: 'primary',
      tldr: 'We do not sell or trade Google user data and limit sharing to essential service providers.',
      ctaText: 'Details',
      content:
        'We do not sell or trade Google user data. We may share Google Calendar data only in the following limited circumstances.',
      bullets: [
        'With cloud service providers (AWS, Firebase) for secure storage and processing',
        'With authorized employees and contractors who are bound by confidentiality agreements',
        'When required by law or legal process with proper legal authorization',
        'We do not share Google user data with advertisers or marketing platforms',
        'We do not share Google user data with third-party applications or services',
        'All sharing is logged and monitored for compliance purposes',
      ],
    },
    {
      id: 'google-data-protection',
      title: 'Storage and Protection of Google User Data',
      icon: <Lock size={24} />,
      type: 'primary',
      tldr: 'Google user data is stored securely with industry-standard encryption and access controls.',
      ctaText: 'Security Measures',
      content:
        'Google user data accessed by Sync and Explore is protected using comprehensive security measures.',
      bullets: [
        'Stored securely using encrypted databases with AES-256 encryption',
        'Protected using industry-standard security measures including SSL/TLS encryption',
        'Accessible only to authorized personnel on a need-to-know basis',
        'Regularly monitored for unauthorized access with intrusion detection systems',
        'We follow data minimization principles and only retain necessary Google user data',
        'All data transfers are encrypted and authenticated',
        'Regular security audits and penetration testing are performed',
      ],
    },
    {
      id: 'google-data-retention',
      title: 'Retention and Deletion of Google User Data',
      icon: <Clock size={24} />,
      type: 'primary',
      tldr: 'Google data is retained only while your account is connected and can be deleted upon request.',
      ctaText: 'Learn More',
      content:
        'Google Calendar data is retained only while your account is active and connected to provide our services.',
      bullets: [
        'Google Calendar data is retained only while your account is active and connected',
        'If you disconnect your Google account, we stop accessing new calendar data immediately',
        'You may request deletion of your Google data at any time through account settings',
        'You can email us at privacy@syncandexplore.com to request data deletion',
        'Upon deletion request, we remove your Google data from active systems within 30 days',
        'Data may be retained longer only if required by law or pending legal action',
        'All deletion requests are processed within the timeframe required by applicable privacy laws',
      ],
    },
    {
      id: 'security',
      title: 'How We Protect Your Data',
      icon: <Lock size={24} />,
      type: 'primary',
      tldr: 'We implement industry-standard security measures to protect your information.',
      ctaText: 'Security Features',
      content:
        'We take data security seriously and implement comprehensive measures to protect your personal information.',
      bullets: [
        'Encryption: All data in transit uses SSL/TLS encryption; sensitive data is encrypted at rest',
        'Access Control: Restricted access to personal data based on role-based permissions',
        'Authentication: Secure password protocols and multi-factor authentication support',
        'Regular Audits: Periodic security assessments and penetration testing by external experts',
        'Data Minimization: We collect only necessary information and delete it when no longer needed',
        'Breach Response: Incident response plan with notification protocols within 72 hours if required',
        'Employee Training: All staff undergo data protection and privacy training',
        'Vendor Security: Contracts with third parties include strict data protection clauses',
        'Monitoring: 24/7 monitoring systems to detect and prevent unauthorized access',
      ],
    },
    {
      id: 'retention',
      title: 'How Long Do We Keep Your Data?',
      icon: <Clock size={24} />,
      type: 'primary',
      tldr: 'We retain data only as long as necessary for the purposes stated.',
      ctaText: 'Learn More',
      content:
        'Our data retention policy ensures we keep information only for as long as it serves a legitimate purpose.',
      bullets: [
        'Active Account Data: Retained as long as your account is active and for 2 years after deletion',
        'Transaction Records: Retained for 7 years for accounting and legal compliance (GDPR/tax requirements)',
        'Marketing Data: Retained until you unsubscribe or 5 years from last engagement',
        'Support Communications: Deleted after 3 years unless longer retention is legally required',
        'Cookies: Typically expire after 12 months; you can clear them anytime',
        'Analytics Data: Aggregated data retained indefinitely; individual identifiers anonymized after 13 months',
        'Failed Login Attempts: Retained for 90 days for security purposes',
        'Backup Data: May be retained longer for disaster recovery (encrypted and segregated)',
        'Legal Holds: Data may be retained indefinitely if required by pending legal action',
      ],
    },
    {
      id: 'cookies',
      title: 'Cookies & Tracking Technologies',
      icon: <Cookie size={24} />,
      type: 'primary',
      tldr: 'We use cookies to enhance your experience and measure platform performance.',
      ctaText: 'Expand',
      content:
        'Like most websites, we use cookies and similar technologies to personalize your experience and gather analytics.',
      bullets: [
        'Essential Cookies: Required for platform functionality, security, and session management',
        'Performance Cookies: Help us understand how you use Sync and Explore (Google Analytics)',
        'Functional Cookies: Remember your preferences and settings (language, timezone, theme)',
        'Marketing Cookies: Track behavior for personalized advertising and retargeting campaigns',
        'Local Storage: Browser data stored locally for improved performance and offline capabilities',
        'Pixels & Beacons: Tiny tracking images used for conversion tracking and behavioral analysis',
        'Your Control: You can disable cookies through browser settings; some features may be limited',
      ],
    },
    {
      id: 'rights',
      title: 'Your Privacy Rights',
      icon: <Shield size={24} />,
      type: 'highlight',
      tldr: 'You have rights regarding your personal data under various privacy laws.',
      ctaText: 'Expand',
      content:
        'Depending on your location, you have various rights regarding your personal data. We are committed to honoring these rights.',
      bullets: [
        'Right to Access: Request a copy of your personal data we hold',
        'Right to Rectification: Correct inaccurate or incomplete data',
        'Right to Erasure (Right to be Forgotten): Request deletion of your data under certain circumstances',
        'Right to Restrict Processing: Limit how we use your information temporarily',
        'Right to Data Portability: Receive your data in a machine-readable format',
        'Right to Object: Opt-out of marketing communications and data processing',
        'Right to Withdraw Consent: Revoke permission for data processing at any time',
        'Right Against Automated Decision-Making: Request human review of AI-driven decisions',
        'Right to Lodge Complaints: Report privacy concerns to data protection authorities',
      ],
    },
  ];

  const policyTitleSections = [
    {
      id: 'gdpr',
      title: 'GDPR Compliance (EU Residents)',
      icon: <MapPin size={24} />,
      type: 'highlight',
      ctaText: 'Learn More',
      subsections: [
        {
          subtitle: 'Legal Basis',
          content:
            'We process your personal data under the following legal bases: (1) Your consent for marketing and non-essential communications; (2) Contractual necessity to provide our services; (3) Legitimate interests in fraud prevention, security, and service improvement; (4) Legal obligation to comply with tax and financial regulations.',
        },
        {
          subtitle: 'Data Protection Officer (DPO)',
          content:
            'Our Data Protection Officer is available to address privacy concerns. You can contact them at privacy@syncandexplore.com. They can help with data requests, privacy inquiries, and compliance concerns.',
        },
        {
          subtitle: 'International Data Transfer',
          content:
            'When we transfer data outside the EU/EEA, we use Standard Contractual Clauses (SCCs) or Binding Corporate Rules (BCRs) to ensure adequate protection. You may request details about safeguards in place.',
        },
      ],
    },
    {
      id: 'ccpa',
      title: 'CCPA Compliance (California Residents)',
      icon: <Key size={24} />,
      type: 'highlight',
      ctaText: 'Learn More',
      subsections: [
        {
          subtitle: 'Consumer Rights',
          content:
            'Under the California Consumer Privacy Act (CCPA), you have the right to: (1) Know what personal information is collected; (2) Delete personal information we collect; (3) Opt-out of the sale of personal information; (4) Opt-out of targeted advertising; (5) Equal service and price even if you exercise your rights.',
        },
        {
          subtitle: 'Privacy Rights Request',
          content:
            'To exercise your rights, submit a request at privacy@syncandexplore.com or through your account settings. We verify your identity before processing requests and respond within 45 days.',
        },
        {
          subtitle: 'Non-Discrimination',
          content:
            'We will not discriminate against you for exercising your privacy rights. You will receive the same service and pricing regardless of whether you choose to exercise your CCPA rights.',
        },
      ],
    },
    {
      id: 'children',
      title: 'Children\'s Privacy',
      icon: <Users size={24} />,
      type: 'primary',
      ctaText: 'Learn More',
      subsections: [
        {
          subtitle: 'Age Restrictions',
          content:
            'Sync and Explore is not intended for users under 13 years old (or the applicable minimum age in your jurisdiction). We do not knowingly collect personal information from children under this age.',
        },
        {
          subtitle: 'Parental Consent',
          content:
            'If you are between 13-18 years old, we require parental/guardian consent for account creation and data collection. Parents can request to review, update, or delete their child\'s data at any time.',
        },
        {
          subtitle: 'Child Safety',
          content:
            'If we discover personal information from a child under 13, we will delete it immediately and inform the parent/guardian. Report any concerns to safety@syncandexplore.com.',
        },
      ],
    },
    {
      id: 'thirdparty',
      title: 'Third-Party Links & Services',
      icon: <Share2 size={24} />,
      type: 'primary',
      ctaText: 'Learn More',
      subsections: [
        {
          subtitle: 'External Links',
          content:
            'Our platform contains links to third-party websites (hotels, airlines, booking platforms). We are not responsible for their privacy practices. Review their privacy policies before sharing information.',
        },
        {
          subtitle: 'Social Media Integration',
          content:
            'If you connect your social media accounts (Facebook, Google, Instagram), those platforms may collect data about your interactions with Sync and Explore. Review their privacy policies for details.',
        },
        {
          subtitle: 'Partner Services',
          content:
            'We may integrate third-party services for analytics, payments, or communications. These partners have their own privacy policies. We encourage you to review them.',
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contact Us About Privacy',
      icon: <Mail size={24} />,
      type: 'primary',
      ctaText: 'Contact Info',
      subsections: [
        {
          subtitle: 'Privacy Team',
          content:
            'If you have questions about this Privacy Policy, concerns about your data, or wish to exercise your privacy rights, contact us at: privacy@syncandexplore.com or submit a form at https://syncandexplore.com/privacy-request',
        },
        {
          subtitle: 'Mailing Address',
          content:
            'Sync and Explore Privacy Team, HNo-337G/F Kh No. 126, Saini Mohalla, Nangloi-III, West Delhi, New Delhi, Delhi, India, 110041',
        },
        {
          subtitle: 'Response Time',
          content:
            'We aim to respond to privacy inquiries within 14 days. Complex requests may take longer, but we\'ll keep you updated on progress.',
        },
      ],
    },
  ];

  const tableOfContents = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'collection', label: 'Information Collection' },
    { id: 'usage', label: 'Data Usage' },
    { id: 'sharing', label: 'Data Sharing' },
    { id: 'security', label: 'Security Measures' },
    { id: 'retention', label: 'Data Retention' },
    { id: 'cookies', label: 'Cookies & Tracking' },
    { id: 'rights', label: 'Your Rights' },
    { id: 'gdpr', label: 'GDPR Compliance' },
    { id: 'ccpa', label: 'CCPA Compliance' },
    { id: 'children', label: 'Children\'s Privacy' },
    { id: 'thirdparty', label: 'Third-Party Services' },
    { id: 'contact', label: 'Contact Us' },
  ];

  return (
    <div className="terms-premium-container">
      {/* Premium Header with Banner */}
      <header className="terms-premium-banner" ref={headerRef}>
        <div className="banner-backdrop-mesh"></div>
        <div className="banner-pattern-overlay"></div>

        <button
          onClick={() => navigate(-1)}
          className="banner-back-btn"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <div className="banner-content">
          <div className="banner-badge-group">
            <span className="last-updated-pill">
              ✓ Updated January 2026 • GDPR & CCPA Compliant
            </span>
          </div>

          <h1 className="banner-title">
            Your Privacy<br />
            <span className="text-highlight">Matters to Us</span>
          </h1>

          <p className="banner-subtitle">
            We're transparent about how we collect, use, and protect your personal data. Read our comprehensive privacy policy to understand your rights and our commitments.
          </p>

          <div className="banner-trust-indicators">
            <div className="trust-item">
              <Lock size={18} />
              <span>Secure</span>
            </div>
            <div className="trust-divider"></div>
            <div className="trust-item">
              <Shield size={18} />
              <span>Compliant</span>
            </div>
            <div className="trust-divider"></div>
            <div className="trust-item">
              <Eye size={18} />
              <span>Transparent</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} />
      </header>

      {/* Main Content */}
      <div className="terms-premium-wrapper">
        {/* Sidebar with Table of Contents */}
        <aside className="terms-sidebar">
          <div className="sidebar-sticky">
            <h3 className="sidebar-title">On This Page</h3>
            <nav className="toc-nav">
              {tableOfContents.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`toc-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const element = sectionsRef.current[item.id];
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setActiveSection(item.id);
                    }
                  }}
                >
                  <span className="toc-dot"></span>
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <main ref={contentRef} className="terms-premium-content">
          <div className="terms-content-wrapper">
            {/* Introduction Section */}
            <section
              className="premium-section intro-premium-section"
              ref={(el) => (sectionsRef.current['introduction'] = el)}
            >
              <div className="section-intro-banner">
                <h2>Welcome to Our Privacy Policy</h2>
                <p>
                  At Sync and Explore, we believe your personal information is just that—personal. This policy explains exactly how we handle your data, and your choices in the matter. We've written this in plain language, not legalese, because we believe privacy should be clear and understandable.
                </p>
              </div>
            </section>

            {/* Main Legal Sections */}
            {mainSections.map((section, index) => (
              <section
                key={section.id}
                ref={(el) => (sectionsRef.current[section.id] = el)}
                className={`premium-legal-card card-type-${section.type}`}
              >
                <div className="card-header-premium">
                  <div className="card-number">{String(index + 1).padStart(2, '0')}</div>
                  <div className="card-title-group">
                    <div className="card-icon">{section.icon}</div>
                    <h2>{section.title}</h2>
                  </div>
                </div>

                <div className="card-summary-box">
                  <p>{section.tldr}</p>
                </div>

                <button
                  className={`expand-toggle ${expandedSections[section.id] ? 'expanded' : ''}`}
                  onClick={() =>
                    setExpandedSections((p) => ({ ...p, [section.id]: !p[section.id] }))
                  }
                >
                  <span>{expandedSections[section.id] ? 'Hide Details' : section.ctaText}</span>
                  <ChevronDown size={18} />
                </button>

                {expandedSections[section.id] && (
                  <div className="card-content-expanded">
                    <p>{section.content}</p>
                    {section.bullets && (
                      <ul className="content-bullets">
                        {section.bullets.map((bullet, i) => (
                          <li key={i}>{bullet}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </section>
            ))}

            {/* Privacy Guardrails Section */}
            <section className="trust-safety-premium">
              <h2>Your Privacy Guardrails</h2>
              <p className="section-subtitle-premium">
                We've built protections directly into how we handle your data.
              </p>

              <div className="trust-grid">
                <div className="trust-card">
                  <div className="trust-card-icon security-icon">
                    <Lock size={28} />
                  </div>
                  <h3>Data Security</h3>
                  <ul>
                    <li>Military-grade encryption</li>
                    <li>Regular security audits</li>
                    <li>Secure payment processing</li>
                    <li>24/7 threat monitoring</li>
                  </ul>
                </div>

                <div className="trust-card">
                  <div className="trust-card-icon privacy-icon">
                    <Eye size={28} />
                  </div>
                  <h3>Transparency</h3>
                  <ul>
                    <li>Clear privacy controls</li>
                    <li>Easy data access</li>
                    <li>Consent management</li>
                    <li>Breach notification</li>
                  </ul>
                </div>

                <div className="trust-card">
                  <div className="trust-card-icon control-icon">
                    <Key size={28} />
                  </div>
                  <h3>Your Control</h3>
                  <ul>
                    <li>Download your data</li>
                    <li>Delete your account</li>
                    <li>Manage permissions</li>
                    <li>Opt-out anytime</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Policy Sections */}
            {policyTitleSections.map((policySection, policyIndex) => (
              <section
                key={policySection.id}
                ref={(el) => (sectionsRef.current[policySection.id] = el)}
                className={`premium-legal-card card-type-${policySection.type}`}
              >
                <div className="card-header-premium">
                  <div className="card-number">
                    {String(mainSections.length + policyIndex + 1).padStart(2, '0')}
                  </div>
                  <div className="card-title-group">
                    <div className="card-icon">{policySection.icon}</div>
                    <h2>{policySection.title}</h2>
                  </div>
                </div>

                <button
                  className={`expand-toggle ${expandedSections[policySection.id] ? 'expanded' : ''}`}
                  onClick={() =>
                    setExpandedSections((p) => ({ ...p, [policySection.id]: !p[policySection.id] }))
                  }
                >
                  <span>{expandedSections[policySection.id] ? 'Hide Details' : policySection.ctaText}</span>
                  <ChevronDown size={18} />
                </button>

                {(expandedSections[policySection.id] || policySection.type === 'highlight') && (
                  <div className="policy-subsections">
                    {policySection.subsections.map((subsection, subIndex) => (
                      <div key={subIndex} className="subsection-premium">
                        <h3>{subsection.subtitle}</h3>
                        <p>{subsection.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* AI & Privacy Section */}
            <section className="ai-disclaimer-premium">
              <div className="disclaimer-icon-box">
                <AlertCircle size={32} />
              </div>
              <div className="disclaimer-content">
                <h3>AI & Personalization</h3>
                <p>
                  Our AI engine analyzes your data to provide personalized travel recommendations. The algorithms respect your privacy preferences and never use your data for purposes you haven't consented to. If you disable data usage, recommendations will be less personalized but still functional. <strong>You maintain full control over your data.</strong>
                </p>
              </div>
            </section>

            {/* CTA Section */}
            <section className="acknowledgement-premium">
              <div className="ack-card-glass">
                <div className="ack-icon-top">
                  <Shield size={32} />
                </div>

                <h2>Got Questions About Your Data?</h2>

                <p className="ack-description">
                  Your privacy is fundamental. If anything in this policy is unclear, or if you have concerns about how we handle your data, our privacy team is ready to help. We're committed to being transparent and responsive to your needs.
                </p>

                <div className="ack-actions">
                  <a href="mailto:privacy@syncandexplore.com" className="ack-cta-premium enabled">
                    <Mail size={20} />
                    <span>Email Our Privacy Team</span>
                  </a>
                  <button
                    className="ack-cta-premium enabled"
                    onClick={() => {
                      const element = sectionsRef.current['contact'];
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    <span>View Contact Information</span>
                    <ChevronUp size={20} style={{ transform: 'rotate(-90deg)' }} />
                  </button>
                </div>

                <p className="ack-support-text">
                  Response time: Within 14 days | Average: 2-3 business days
                </p>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Floating Back to Top */}
      {showScrollTop && (
        <button
          className="floating-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <ChevronUp size={22} />
        </button>
      )}
    </div>
  );
};

export default PrivacyPolicy;
